//
// # HelpPanel
//
// A searchable help panel that displays GenAlpha documentation.
// Users can search for features, keyboard shortcuts, and how-to guides.
// Topics are expandable to show detailed content with collapsible sections.
//

import React, { useState, useCallback } from "react";
import { Search, ChevronRight, ChevronDown } from "lucide-react";
import {
  HELP_CONTENT,
  type HelpCategory,
  type HelpTopic,
  type HelpSection,
} from "./helpContent";

interface HelpSectionItemProps {
  section: HelpSection;
  expanded: boolean;
  onToggle: () => void;
}

function HelpSectionItem({
  section,
  expanded,
  onToggle,
}: HelpSectionItemProps) {
  return (
    <div className="border-t border-[var(--color-ide-border)] first:border-t-0">
      <button
        className="w-full text-left px-2 py-1.5 hover:bg-[var(--color-ide-hover)] transition-colors flex items-start gap-2"
        onClick={onToggle}
      >
        <span className="mt-0.5 text-[var(--color-ide-text-muted)]">
          {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        </span>
        <span className="text-xs text-[var(--color-ide-text)]">
          {section.title}
        </span>
      </button>
      {expanded && (
        <div className="px-2 pb-2 pl-6">
          <pre className="text-xs text-[var(--color-ide-text)] whitespace-pre-wrap font-[inherit] leading-relaxed">
            {section.content}
          </pre>
        </div>
      )}
    </div>
  );
}

interface HelpTopicItemProps {
  topic: HelpTopic;
  categoryKey: string;
  expandedTopics: Set<string>;
  expandedSections: Set<string>;
  onToggleTopic: (key: string) => void;
  onToggleSection: (key: string) => void;
}

function HelpTopicItem({
  topic,
  categoryKey,
  expandedTopics,
  expandedSections,
  onToggleTopic,
  onToggleSection,
}: HelpTopicItemProps) {
  const topicKey = `${categoryKey}-${topic.title}`;
  const isTopicExpanded = expandedTopics.has(topicKey);

  return (
    <div className="border-b border-[var(--color-ide-border)] last:border-b-0">
      <button
        className="w-full text-left px-3 py-2 hover:bg-[var(--color-ide-hover)] transition-colors flex items-start gap-2"
        onClick={() => onToggleTopic(topicKey)}
      >
        <span className="mt-0.5 text-[var(--color-ide-text-muted)]">
          {isTopicExpanded ? (
            <ChevronDown size={14} />
          ) : (
            <ChevronRight size={14} />
          )}
        </span>
        <div className="flex-1">
          <div className="text-sm text-[var(--color-ide-text)]">
            {topic.title}
          </div>
          <div className="text-xs text-[var(--color-ide-text-muted)] mt-0.5">
            {topic.description}
          </div>
        </div>
      </button>
      {isTopicExpanded && (
        <div className="pl-5 pr-3 pb-2 bg-[var(--color-ide-bg)]">
          {topic.sections.map((section) => {
            const sectionKey = `${topicKey}-${section.title}`;
            return (
              <HelpSectionItem
                key={sectionKey}
                section={section}
                expanded={expandedSections.has(sectionKey)}
                onToggle={() => onToggleSection(sectionKey)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

function filterHelpContent(
  categories: HelpCategory[],
  query: string
): HelpCategory[] {
  if (!query) return categories;

  const lowerQuery = query.toLowerCase();

  return categories
    .map((category) => ({
      ...category,
      items: category.items.filter((topic) => {
        // Check topic title and description
        if (topic.title.toLowerCase().includes(lowerQuery)) return true;
        if (topic.description.toLowerCase().includes(lowerQuery)) return true;

        // Check keywords
        if (topic.keywords.some((k) => k.includes(lowerQuery))) return true;

        // Check section titles and content
        for (const section of topic.sections) {
          if (section.title.toLowerCase().includes(lowerQuery)) return true;
          if (section.content.toLowerCase().includes(lowerQuery)) return true;
        }

        return false;
      }),
    }))
    .filter((category) => category.items.length > 0);
}

export function HelpPanel() {
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedTopics, setExpandedTopics] = useState<Set<string>>(new Set());
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set()
  );

  const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  }, []);

  const toggleTopic = useCallback((key: string) => {
    setExpandedTopics((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const toggleSection = useCallback((key: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const filteredContent = filterHelpContent(HELP_CONTENT, searchQuery);

  return (
    <div className="flex flex-col h-full bg-[var(--color-ide-surface)] text-[var(--color-ide-text)]">
      {/* Search bar */}
      <div className="p-3 border-b border-[var(--color-ide-border)]">
        <div className="relative">
          <Search
            size={14}
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--color-ide-text-muted)]"
          />
          <input
            type="text"
            placeholder="Search help topics..."
            value={searchQuery}
            onChange={handleSearch}
            className="w-full pl-8 pr-3 py-1.5 text-sm bg-[var(--color-ide-input)] border border-[var(--color-ide-border)] rounded focus:outline-none focus:border-[var(--color-ide-accent)] text-[var(--color-ide-text)] placeholder-[var(--color-ide-text-muted)]"
          />
        </div>
      </div>

      {/* Help content */}
      <div className="flex-1 overflow-auto">
        {filteredContent.length === 0 ? (
          <div className="text-center py-8 text-[var(--color-ide-text-muted)]">
            <p className="text-sm">No matching topics found.</p>
            <p className="text-xs mt-2">
              Try different keywords or ask the AI Assistant.
            </p>
          </div>
        ) : (
          filteredContent.map((category) => (
            <div key={category.category} className="mb-2">
              <div className="flex items-center gap-2 px-3 py-2 text-[10px] font-semibold uppercase tracking-widest text-[var(--color-ide-text-muted)] bg-[var(--color-ide-surface-2)] sticky top-0">
                <category.icon size={12} />
                {category.category}
              </div>
              <div>
                {category.items.map((topic) => (
                  <HelpTopicItem
                    key={`${category.category}-${topic.title}`}
                    topic={topic}
                    categoryKey={category.category}
                    expandedTopics={expandedTopics}
                    expandedSections={expandedSections}
                    onToggleTopic={toggleTopic}
                    onToggleSection={toggleSection}
                  />
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Footer with link to full docs */}
      <div className="p-3 border-t border-[var(--color-ide-border)]">
        <p className="text-xs text-[var(--color-ide-text-muted)] text-center">
          For detailed help, ask the{" "}
          <span className="text-[var(--color-ide-accent)]">AI Assistant</span>{" "}
          in the right panel.
        </p>
      </div>
    </div>
  );
}
